<template>
  <layout-header></layout-header>
  <layout-sidebar></layout-sidebar>

  <div class="page-wrapper">
    <div class="content">
      <div class="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
        <breadcrumb :title="title" :text="text" :text1="text1" />
        <div class="d-flex my-xl-auto right-content align-items-center flex-wrap dq-page-actions">
          <div class="me-2 mb-2">
            <select v-model="filters.frequency" class="form-select">
              <option value="monthly">Monthly</option>
              <option value="weekly">Weekly</option>
              <option value="fortnightly">Fortnightly</option>
              <option value="daily">Daily</option>
            </select>
          </div>
          <div class="me-2 mb-2" v-if="filters.frequency === 'monthly'">
            <input v-model="filters.period" type="month" class="form-control" />
          </div>
          <div class="me-2 mb-2" v-else>
            <input v-model="filters.period_start" type="date" class="form-control" />
          </div>
          <div class="me-2 mb-2" v-if="filters.frequency !== 'monthly'">
            <input v-model="filters.period_end" type="date" class="form-control" />
          </div>
          <div class="me-2 mb-2">
            <select v-model="filters.status" class="form-select">
              <option value="">All Statuses</option>
              <option value="pending">Pending</option>
              <option value="paid">Paid</option>
            </select>
          </div>
          <div class="me-2 mb-2">
            <input v-model.trim="filters.search" type="text" class="form-control" placeholder="Search employee, receiver, ref" />
          </div>
          <div class="me-2 mb-2">
            <button class="btn btn-primary" :disabled="loading" @click="fetchReport">
              <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>
              Load
            </button>
          </div>
          <div class="mb-2">
            <div class="dropdown">
              <a class="dropdown-toggle btn btn-white d-inline-flex align-items-center" data-bs-toggle="dropdown">
                <i class="ti ti-file-export me-1"></i>Export
              </a>
              <ul class="dropdown-menu dropdown-menu-end p-3">
                <li>
                  <a class="dropdown-item rounded-1" href="javascript:void(0);" @click.prevent="exportReport('csv')">
                    <i class="ti ti-file-type-xls me-1"></i>Export as CSV
                  </a>
                </li>
                <li>
                  <a class="dropdown-item rounded-1" href="javascript:void(0);" @click.prevent="exportReport('pdf')">
                    <i class="ti ti-file-type-pdf me-1"></i>Export as PDF
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      <div v-if="summary" class="row dq-stat-grid">
        <div class="col-xl-3 col-md-6 d-flex mb-3" v-for="card in cards" :key="card.title">
          <div class="card flex-fill dq-stat-card">
            <div class="card-body">
              <div class="d-flex align-items-center justify-content-between bg-light border rounded p-2 mb-2">
                <div>
                  <span class="fs-14 fw-normal text-truncate mb-1">{{ card.title }}</span>
                  <h5>{{ card.isMoney ? `${summary.currency} ${formatMoney(card.value)}` : card.value }}</h5>
                </div>
                <a href="javascript:void(0);" class="avatar avatar-md avatar-rounded" :class="`bg-transparent-${card.cls} border border-${card.cls}`">
                  <span :class="`text-${card.cls}`"><i :class="card.icon"></i></span>
                </a>
              </div>
              <p class="fs-12 fw-normal text-muted mb-0">{{ card.note }}</p>
            </div>
          </div>
        </div>
      </div>

      <div class="alert alert-secondary">
        This screen is the explicit cash disbursement audit trail. It shows who confirmed cash payment, when they did it, and which payroll period the payout belonged to.
      </div>

      <div class="card">
        <div class="card-header">
          <h5 class="mb-0">Cash Payroll Audit Trail</h5>
        </div>
        <div class="card-body p-0">
          <div class="table-responsive">
            <table class="table table-striped align-middle mb-0">
              <thead class="thead-light">
                <tr>
                  <th>Period</th>
                  <th>Emp ID</th>
                  <th>Name</th>
                  <th>Amount</th>
                  <th>Status</th>
                  <th>Approval</th>
                  <th>Paid At</th>
                  <th>Acknowledged</th>
                  <th>Paid By</th>
                  <th>Reference</th>
                  <th>Receiver</th>
                  <th>Updated</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loading">
                  <td colspan="12" class="text-center py-4">Loading cash payout audit trail...</td>
                </tr>
                <tr v-else-if="!rows.length">
                  <td colspan="12" class="text-center py-4">No cash payout audit rows found.</td>
                </tr>
                <tr v-for="row in rows" :key="row.id">
                  <td>{{ row.period_label || row.period || "-" }}</td>
                  <td>{{ row.employee_id }}</td>
                  <td>{{ row.employee_name }}</td>
                  <td>{{ row.currency }} {{ formatMoney(row.amount) }}</td>
                  <td>
                    <span class="badge" :class="row.status === 'paid' ? 'badge-soft-success' : 'badge-soft-warning'">
                      {{ row.status === "paid" ? "Paid" : "Pending" }}
                    </span>
                  </td>
                  <td>
                    <span class="badge" :class="row.approval_status === 'approved' ? 'badge-soft-success' : 'badge-soft-danger'">
                      {{ row.approval_status === "approved" ? "Approved" : "Blocked" }}
                    </span>
                  </td>
                  <td>{{ formatDateTime(row.paid_at) }}</td>
                  <td>{{ formatDateTime(row.acknowledged_at) }}</td>
                  <td>{{ row.paid_by_name || "-" }}</td>
                  <td>{{ row.payment_reference || "-" }}</td>
                  <td>{{ row.receiver_name || "-" }}</td>
                  <td>{{ formatDateTime(row.updated_at) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>

    <div class="footer d-sm-flex align-items-center justify-content-between border-top bg-white p-3">
      <p class="mb-0">2014 - {{ new Date().getFullYear() }} &copy; DoneQuick HR & Payroll</p>
      <p>Powered by <a href="#" @click.prevent class="text-primary">DoneQuick Technological World</a></p>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, reactive, ref } from "vue";
import moment from "moment";
import reportsService from "@/services/reports.service";
import { toast } from "@/utils/toast";

const title = "Cash Payroll Audit";
const text = "Administration";
const text1 = "Cash Payroll Audit";

const loading = ref(false);
const summary = ref(null);
const rows = ref([]);

const filters = reactive({
  frequency: "monthly",
  period: moment().format("YYYY-MM"),
  period_start: moment().startOf("week").format("YYYY-MM-DD"),
  period_end: moment().startOf("week").add(6, "days").format("YYYY-MM-DD"),
  status: "",
  search: "",
});

const cards = computed(() => {
  if (!summary.value) return [];
  return [
    {
      title: "Audit Entries",
      value: summary.value.entry_count || 0,
      cls: "primary",
      icon: "ti ti-history",
      note: `Filtered period: ${summary.value.period}`,
    },
    {
      title: "Paid Amount",
      value: summary.value.paid_amount || 0,
      cls: "success",
      icon: "ti ti-cash-banknote",
      isMoney: true,
      note: `${summary.value.paid_count || 0} payouts confirmed`,
    },
    {
      title: "Pending Amount",
      value: summary.value.pending_amount || 0,
      cls: "warning",
      icon: "ti ti-hourglass-empty",
      isMoney: true,
      note: `${summary.value.pending_count || 0} payouts still pending`,
    },
    {
      title: "Latest Payout",
      value: summary.value.latest_paid_at ? moment(summary.value.latest_paid_at).format("DD MMM YYYY") : "-",
      cls: "info",
      icon: "ti ti-calendar-time",
      note: summary.value.latest_paid_at ? moment(summary.value.latest_paid_at).format("HH:mm") : "No paid rows in filter",
    },
  ];
});

function formatMoney(value) {
  return Number(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatDateTime(value) {
  return value ? moment(value).format("YYYY-MM-DD HH:mm") : "-";
}

function downloadBlob(blob, filename) {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

async function fetchReport() {
  loading.value = true;
  try {
    const params = buildParams();
    const { data } = await reportsService.cashPayrollAudit({
      ...params,
      status: filters.status || undefined,
      search: filters.search || undefined,
    });
    summary.value = data.summary || null;
    rows.value = data.rows || [];
  } catch (err) {
    toast(err?.response?.data?.message || "Failed to load cash payroll audit trail.", "error");
  } finally {
    loading.value = false;
  }
}

async function exportReport(format) {
  try {
    const params = buildParams();
    const res =
      format === "pdf"
        ? await reportsService.exportCashPayrollAuditPDF({
            ...params,
            status: filters.status || undefined,
            search: filters.search || undefined,
          })
        : await reportsService.exportCashPayrollAuditCSV({
            ...params,
            status: filters.status || undefined,
            search: filters.search || undefined,
          });

    downloadBlob(res.data, `cash-payroll-audit-${reportPeriodToken()}.${format}`);
  } catch (err) {
    toast(err?.response?.data?.message || "Failed to export cash payroll audit trail.", "error");
  }
}

function buildParams() {
  if (filters.frequency === "monthly") {
    if (!filters.period) {
      return {};
    }

    return {
      frequency: "monthly",
      period: filters.period,
    };
  }

  if (!filters.period_start || !filters.period_end) {
    return {};
  }

  return {
    frequency: filters.frequency,
    period_start: filters.period_start,
    period_end: filters.period_end,
  };
}

function reportPeriodToken() {
  return filters.frequency === "monthly"
    ? filters.period || "all-periods"
    : [filters.frequency, filters.period_start, filters.period_end].filter(Boolean).join("-");
}

onMounted(fetchReport);
</script>


